import {useCallback, useMemo} from 'react';
import {
  DEAL_STATUSES,
  Offer,
  OFFER_CATEGORIES,
  OFFER_CONFIRMATION_TYPES,
  UNIT_PRICE_TYPES,
} from '@chekin/core';
import {useReservation} from 'context/reservation';
import {useCreateBulkDealMutation} from './useCreateBulkDealMutation';
import {useFetchDeals} from './useFetchDeals';
import {useFetchOffers} from './useFetchOffers';
import {useGuestGroup} from './useGuestGroup';
import {useMutateDeals} from './useMutateDeals';
import {useSummary} from 'context/summary';
import {isFirstLoad} from 'utils/query';

const FIXED_PET_FEE_QUANTITY = 1;
const EDITABLE_PET_FEE_DEAL_STATUSES = [
  DEAL_STATUSES.pre_requested,
  DEAL_STATUSES.approved,
];

const isPetFeeOffer = (offer?: Offer) => offer?.category === OFFER_CATEGORIES.petFee;

const getPetFeeQuantity = (offer: Offer, numberOfPets: number) => {
  const unitType = offer.items[0]?.unit_type;
  const isPerPetOrUnit =
    unitType === UNIT_PRICE_TYPES.pet || unitType === UNIT_PRICE_TYPES.unit;

  return isPerPetOrUnit ? numberOfPets : FIXED_PET_FEE_QUANTITY;
};

function usePetFeeDeal() {
  const {data: reservation} = useReservation();
  const {guestGroup} = useGuestGroup();
  const offersQuery = useFetchOffers({
    housingId: reservation?.housing_id,
    category: OFFER_CATEGORIES.petFee,
  });
  const approvedDealsQuery = useFetchDeals({
    reservationId: reservation?.id,
    category: OFFER_CATEGORIES.petFee,
    status: DEAL_STATUSES.approved,
  });
  const preRequestedDealsQuery = useFetchDeals({
    reservationId: reservation?.id,
    category: OFFER_CATEGORIES.petFee,
    status: DEAL_STATUSES.pre_requested,
  });
  const paidDealsQuery = useFetchDeals({
    reservationId: reservation?.id,
    category: OFFER_CATEGORIES.petFee,
    status: DEAL_STATUSES.paid,
  });
  const paginatedPetFeeOffers = offersQuery.data;
  const approvedPetFeeDeals = approvedDealsQuery.data;
  const preRequestedPetFeeDeals = preRequestedDealsQuery.data;
  const paidPetFeeDeals = paidDealsQuery.data;
  const {data: summary} = useSummary();
  const {mutateAsync: createDeal, isPending: isCreatingPetFeeDeal} =
    useCreateBulkDealMutation();
  const {deleteDealAsyncMutation, isDeletingDeal: isDeletingPetFeeDeal} =
    useMutateDeals();

  const petFeeOffer = useMemo(() => {
    return (
      summary?.upselling?.active && paginatedPetFeeOffers?.results.find(isPetFeeOffer)
    );
  }, [summary?.upselling?.active, paginatedPetFeeOffers?.results]);

  const editablePetFeeDeal = useMemo(() => {
    const editablePetFeeDeals = [
      ...(approvedPetFeeDeals?.results ?? []),
      ...(preRequestedPetFeeDeals?.results ?? []),
    ];

    return editablePetFeeDeals.find(deal =>
      EDITABLE_PET_FEE_DEAL_STATUSES.includes(deal.status),
    );
  }, [approvedPetFeeDeals?.results, preRequestedPetFeeDeals?.results]);

  const hasPaidPetFeeDeal = Boolean(paidPetFeeDeals?.count);

  const createPetFeeDeal = useCallback(
    async (numberOfPets?: number) => {
      if (
        !reservation ||
        !petFeeOffer ||
        hasPaidPetFeeDeal ||
        isCreatingPetFeeDeal ||
        isDeletingPetFeeDeal
      ) {
        return;
      }

      try {
        if (editablePetFeeDeal) {
          await deleteDealAsyncMutation(editablePetFeeDeal.id);
        }

        if (!numberOfPets || numberOfPets < 1) {
          return;
        }

        const requestedItem = petFeeOffer.items[0];
        if (!requestedItem) {
          return;
        }

        const coreGuest = guestGroup?.members.find(
          ({id}) => id === guestGroup?.leader_id,
        );
        const quantity = getPetFeeQuantity(petFeeOffer, numberOfPets);
        const dealPayload = {
          core_housing_id: reservation.housing_id,
          core_reservation_id: reservation.id,
          core_guest_name: coreGuest?.full_name,
          core_housing_name: reservation.housing_name,
          core_room_numbers: reservation.rooms.map(room => room.number),
          guest_email: coreGuest?.email || reservation.default_invite_email,
          core_guest_id: coreGuest?.id,
          offer_id: petFeeOffer.id,
          quantity: FIXED_PET_FEE_QUANTITY,
          payments_version: 'V2' as const,
          number_of_people: quantity,
          requested_items: [
            {
              item: requestedItem.id,
              quantity,
            },
          ],
          check_in_date: reservation.check_in_date?.slice(0, 10) ?? null,
          check_out_date: reservation.check_out_date?.slice(0, 10) ?? null,
          status:
            petFeeOffer.confirmation_type === OFFER_CONFIRMATION_TYPES.auto
              ? DEAL_STATUSES.approved
              : DEAL_STATUSES.pre_requested,
        };

        await createDeal({
          deals: [dealPayload],
        });
      } catch {
        return;
      }
    },
    [
      createDeal,
      deleteDealAsyncMutation,
      editablePetFeeDeal,
      guestGroup,
      hasPaidPetFeeDeal,
      isCreatingPetFeeDeal,
      isDeletingPetFeeDeal,
      petFeeOffer,
      reservation,
    ],
  );

  return {
    createPetFeeDeal,
    hasPetFeeOffer: Boolean(petFeeOffer),
    isLoadingPetFeeOffer: [
      offersQuery,
      approvedDealsQuery,
      preRequestedDealsQuery,
      paidDealsQuery,
    ].some(isFirstLoad),
    isCreatingPetFeeDeal: isCreatingPetFeeDeal || isDeletingPetFeeDeal,
  };
}

export {usePetFeeDeal};
